import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    Form, Input, InputNumber, Select, Switch, Button, Upload, Table, Checkbox,
    Typography, Divider, Space, Row, Col, Alert, notification, AutoComplete,
} from 'antd';
import { UploadOutlined, DeleteOutlined, PlusOutlined, ThunderboltOutlined, ReloadOutlined } from '@ant-design/icons';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { TemplatesProxy } from '../services/catalogServiceAPI/templatesProxy';
import { CategoriesProxy } from '../services/catalogServiceAPI/categoriesProxy';
import { loadFacetVocab } from '../services/catalogServiceAPI/facetVocabProxy';
import { CatalogFacetsProxy } from '../services/catalogServiceAPI/catalogFacetsProxy';
import { noNonBmpRule } from '../utils/textValidation';
import { isoToUtcInput, utcInputToIso } from '../utils/dateFormatting';
import { getSpecialDay } from './specialDays';
import { AvatarItemSelect } from '../avatarItems/AvatarItemSelect';
import { config } from '../../config';
import videoFiltersJson from '@designwithfriends/common/render/videoFilters.json';

const { Title, Text } = Typography;

const TIERS = ['gold', 'silver', 'bronze', 'participant'];

const DEFAULT_CUSTOM_GRADIENT = { from: '#7c4dff', to: '#f06292' };

const BADGE_PRESET_CONFIG = {
    'x2-rewards': {
        selectLabel: 'Weekend bonus',
        defaultLabel: 'Weekend challenge · 2× rewards',
        gradient: { from: '#ff9800', to: '#ff5722' },
    },
};

const BADGE_PRESET_OPTIONS = [
    { value: '', label: '(custom)' },
    ...Object.entries(BADGE_PRESET_CONFIG).map(([value, c]) => ({ value, label: c.selectLabel })),
];

function badgePreviewBackground(badge) {
    const presetGradient = badge?.preset ? BADGE_PRESET_CONFIG[badge.preset]?.gradient : null;
    const { from, to } = presetGradient ?? badge?.gradient ?? DEFAULT_CUSTOM_GRADIENT;
    return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

const ROOM_TYPE_OPTIONS = [
    'Master Bedroom',
    'Kids Bedroom',
    'Nursery',
    'Living Room',
    'Dining Room',
    'Kitchen',
    'Guest Bedroom',
    'Home Office',
    'Playroom',
    'Game Room',
    'Family Room',
    'Reading Room',
    'Study Room',
    'Guest Bathroom',
    'Meditation Room',
    'Teen Bedroom',
    'Baby Room',
    'Apartment Studio',
    'Loft',
    'Hobby Room',
    'Lounge Room',
    'Entryway',
].map(value => ({ value, label: value }));

const DEFAULTS = {
    type: 'timebased',
    status: 'draft',
    duration: 24,
    votingDuration: 24,
    entryCost: { coins: 0, gems: 0 },
    rewards: {
        gold:        { coins: 500, gems: 5 },
        silver:      { coins: 250, gems: 2 },
        bronze:      { coins: 100, gems: 1 },
        participant: { coins: 50,  gems: 0 },
    },
    badges: [],
    roomRequirements: {
        minItems: 5,
        restrictions: [],
        catalogLocks: [],
        templateItemLocks: [],
    },
    renderRequirements: { requiredMediaType: 'image' },
    notification: { message: '' },
};

// Mirrors RenderEnvironment in packages/common/render/Rendering.d.ts; any other text is used as-is in the prompt.
const WINDOW_VIEW_OPTIONS = [
    { value: 'countryside', label: 'Countryside (free)' },
    { value: 'urban', label: 'Urban (free)' },
    { value: 'beach', label: 'Beach (premium)' },
    { value: 'lakeside', label: 'Lakeside (premium)' },
    { value: 'mountains', label: 'Mountains (premium)' },
    { value: 'forest', label: 'Forest (premium)' },
    { value: 'desert', label: 'Desert (premium)' },
    { value: 'tropical', label: 'Tropical (premium)' },
    { value: 'snow', label: 'Snowy landscape (premium)' },
    { value: 'vineyard', label: 'Vineyard hills (premium)' },
];

const MEDIA_TYPE_OPTIONS = [
    { value: 'image', label: 'Photo' },
    { value: 'video', label: 'Video' },
    { value: 'any', label: "User's choice" },
];

const VIDEO_FILTER_OPTIONS = videoFiltersJson.filters.map((f) => ({ value: f.id, label: f.label, description: f.description }));

function suggestedNotification(title) {
    return title ? `Your ${title} results are in!` : '';
}

/** Resolve a category id to its full breadcrumb path (e.g. "Walls › Paint"). */
function categoryPath(categoryId, byId) {
    if (!categoryId) return '';
    const chain = [];
    let cursor = byId.get(categoryId);
    while (cursor) {
        chain.unshift(cursor.title);
        cursor = cursor.parentId ? byId.get(cursor.parentId) : null;
    }
    return chain.join(' › ');
}

/**
 * Flatten the category tree to options with the full breadcrumb path as label, so an
 * AntD Select can disambiguate same-named categories (e.g. "Chairs" under Seating vs Office).
 * Includes both leaf and parent categories — a rule on a parent applies to all descendants.
 */
function buildCategoryOptions(categories) {
    const byId = new Map(categories.map(c => [c.id, c]));
    return categories
        .map(c => ({ value: c.id, label: categoryPath(c.id, byId) }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

function defaultLockedMessage(categoryTitle) {
    if (categoryTitle) {
        return `${categoryTitle} is fixed for this challenge — you can't change it.`;
    }
    return `This category is locked for this challenge.`;
}

const RESTRICTION_FILTER_PROPS = ['categoryId', 'color', 'material', 'style', 'roomType', 'tag'];

function restrictionHasAnyProp(r) {
    return RESTRICTION_FILTER_PROPS.some(prop => (prop === 'tag' ? r?.tag?.trim() : r?.[prop]));
}

/**
 * Auto-suggested end-user copy:
 *   "Place at least <n> [blue | wood | eclectic...] item(s)"
 *   "Place at least <n> item(s) from <Category>"
 * (combined when a restriction has both facets and a category).
 */
function suggestRestrictionMessage(r, vocab, categoriesById) {
    const count = Math.max(1, Number(r?.minItems) || 1);
    const label = (list, id) => list?.find(e => e.id === id)?.label?.toLowerCase() ?? id;
    const adjectives = [
        r.color ? label(vocab?.colorBuckets, r.color) : null,
        r.material ? label(vocab?.materials, r.material) : null,
        r.style ? label(vocab?.styles, r.style) : null,
        r.roomType ? label(vocab?.roomTypes, r.roomType) : null,
        r.tag?.trim() || null,
    ].filter(Boolean);
    const noun = count === 1 ? 'item' : 'items';
    const categoryTitle = r.categoryId ? categoriesById.get(r.categoryId)?.title : null;
    const from = categoryTitle ? ` from ${categoryTitle}` : '';
    return `Place at least ${count} ${[...adjectives, noun].join(' ')}${from}`;
}

export function ChallengeForm({ initialValues, onSubmit, submitLabel, enableSuggest }) {
    const [form] = Form.useForm();
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoriesById, setCategoriesById] = useState(new Map());
    const [image, setImage] = useState(initialValues?.image ?? '');
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [templateItems, setTemplateItems] = useState([]);
    const [templateItemsLoading, setTemplateItemsLoading] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [generatingImage, setGeneratingImage] = useState(false);
    const [suggestingMessages, setSuggestingMessages] = useState(false);
    const [imagePrompt, setImagePrompt] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [suggestedTheme, setSuggestedTheme] = useState('');
    const [suggestRoomType, setSuggestRoomType] = useState(null);
    const [suggestSeed, setSuggestSeed] = useState('');
    const watchedStartDate = Form.useWatch('startDate', form);
    const watchedBadges = Form.useWatch('badges', form) ?? [];
    const specialDay = getSpecialDay(watchedStartDate);
    const notificationTouched = useRef(Boolean(initialValues?.notification?.message));
    const [facetVocab, setFacetVocab] = useState(null);
    const [allTags, setAllTags] = useState([]);
    const [tagsByCategory, setTagsByCategory] = useState({});
    // Last auto-suggested message per restriction row — a row whose message still equals
    // its last suggestion keeps auto-updating; a manually edited message is left alone.
    const restrictionAutoMsgs = useRef({});
    const tagFetchesRequested = useRef(new Set());
    const watchedRestrictions = Form.useWatch(['roomRequirements', 'restrictions'], form) ?? [];
    const watchedCatalogLocks = Form.useWatch(['roomRequirements', 'catalogLocks'], form) ?? [];
    const watchedMediaType = Form.useWatch(['renderRequirements', 'requiredMediaType'], form);
    const watchedVideoFilter = Form.useWatch(['renderRequirements', 'videoFilter'], form);
    const challengesProxy = useMemo(() => new ChallengesProxy(), []);
    const templatesProxy = useMemo(() => new TemplatesProxy(), []);
    const categoriesProxy = useMemo(() => new CategoriesProxy(), []);
    const catalogFacetsProxy = useMemo(() => new CatalogFacetsProxy(), []);

    const ensureCategoryTags = useMemo(() => (categoryId) => {
        if (!categoryId || tagFetchesRequested.current.has(categoryId)) return;
        tagFetchesRequested.current.add(categoryId);
        catalogFacetsProxy.topTags(categoryId, 10)
            .then(tags => setTagsByCategory(prev => ({ ...prev, [categoryId]: tags })))
            // On failure the row falls back to the all-catalog tag list.
            .catch(() => tagFetchesRequested.current.delete(categoryId));
    }, [catalogFacetsProxy]);

    const loadTemplates = useMemo(() => () => {
        setTemplatesLoading(true);
        return templatesProxy.list()
            .then(list => setTemplates(list.map(t => ({ value: t.id, label: t.name }))))
            .catch(e => notification.error({ message: 'Failed to load templates', description: e.message }))
            .finally(() => setTemplatesLoading(false));
    }, [templatesProxy]);

    useEffect(() => {
        void loadTemplates();

        categoriesProxy.list()
            .then(list => {
                setCategoryOptions(buildCategoryOptions(list));
                setCategoriesById(new Map(list.map(c => [c.id, c])));
            })
            .catch(e => notification.error({ message: 'Failed to load categories', description: e.message }));

        loadFacetVocab()
            .then(setFacetVocab)
            .catch(e => notification.error({ message: 'Failed to load facet vocab', description: e.message }));

        catalogFacetsProxy.topTags(null)
            .then(setAllTags)
            .catch(e => notification.error({ message: 'Failed to load catalog tags', description: e.message }));

        for (const r of initialValues?.roomRequirements?.restrictions ?? []) {
            if (r?.categoryId) ensureCategoryTags(r.categoryId);
        }
    }, [loadTemplates, categoriesProxy]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleValuesChange = (changed) => {
        const changedRestrictions = changed?.roomRequirements?.restrictions;
        if (Array.isArray(changedRestrictions)) {
            const all = form.getFieldValue(['roomRequirements', 'restrictions']) ?? [];
            const next = all.slice();
            let mutated = false;
            changedRestrictions.forEach((changedRow, i) => {
                if (!changedRow || typeof changedRow !== 'object') return;
                const row = next[i];
                if (!row) return;
                if ('categoryId' in changedRow && changedRow.categoryId) ensureCategoryTags(changedRow.categoryId);
                // A direct message edit is manual — never overwrite it here.
                if ('message' in changedRow) return;
                const suggestion = suggestRestrictionMessage(row, facetVocab, categoriesById);
                if (!row.message || row.message === restrictionAutoMsgs.current[i]) {
                    next[i] = { ...row, message: suggestion };
                    restrictionAutoMsgs.current[i] = suggestion;
                    mutated = true;
                }
            });
            if (mutated) form.setFieldValue(['roomRequirements', 'restrictions'], next);
        }

        const changedLocks = changed?.roomRequirements?.catalogLocks;
        if (Array.isArray(changedLocks)) {
            const all = form.getFieldValue(['roomRequirements', 'catalogLocks']) ?? [];
            const next = all.slice();
            let mutated = false;
            changedLocks.forEach((changedRow, i) => {
                if (!changedRow || !changedRow.categoryId) return;
                const row = next[i];
                if (!row || row.lockedMessage) return;
                const title = categoriesById.get(row.categoryId)?.title;
                next[i] = { ...row, lockedMessage: defaultLockedMessage(title) };
                mutated = true;
            });
            if (mutated) form.setFieldValue(['roomRequirements', 'catalogLocks'], next);
        }
    };

    // When templateId is (re)selected, load its item list so the admin can configure locks.
    const loadTemplateItems = async (templateId) => {
        if (!templateId) { setTemplateItems([]); return; }
        setTemplateItemsLoading(true);
        try {
            setTemplateItems(await challengesProxy.getTemplateItems(templateId));
        } catch (e) {
            notification.error({ message: 'Failed to load template items', description: e.message });
        } finally {
            setTemplateItemsLoading(false);
        }
    };

    useEffect(() => {
        const initialTemplateId = initialValues?.roomRequirements?.templateId;
        if (initialTemplateId) void loadTemplateItems(initialTemplateId);
    }, [initialValues?.roomRequirements?.templateId]); // eslint-disable-line react-hooks/exhaustive-deps

    // For new challenges, prefill the start date the same way Suggest would
    // (chains from the latest existing challenge's submission close time).
    useEffect(() => {
        if (!enableSuggest || initialValues?.startDate) return;
        let cancelled = false;
        challengesProxy.suggestStartDate()
            .then(({ startDate }) => {
                if (cancelled) return;
                form.setFieldValue('startDate', isoToUtcInput(startDate));
            })
            .catch(e => notification.error({ message: 'Failed to compute start date', description: e.message }));
        return () => { cancelled = true; };
    }, [enableSuggest, initialValues?.startDate, challengesProxy, form]);

    const handleTitleChange = (e) => {
        if (notificationTouched.current) return;
        form.setFieldValue(['notification', 'message'], suggestedNotification(e.target.value));
    };

    const handleUpload = async (file) => {
        setUploading(true);
        try {
            const { url } = await challengesProxy.uploadImage(file);
            setImage(url);
            form.setFieldValue('image', url);
        } catch (e) {
            notification.error({ message: 'Upload failed', description: e.message });
        } finally {
            setUploading(false);
        }
        return false;
    };

    const handleSuggest = async () => {
        setSuggesting(true);
        try {
            const seed = suggestSeed.trim();
            // If the start date lands on a holiday, weave it into the seed so GPT themes around it.
            const augmentedSeed = specialDay
                ? (seed ? `${seed} (also: ${specialDay})` : specialDay)
                : (seed || undefined);
            const s = await challengesProxy.suggestConcept({
                roomType: suggestRoomType || undefined,
                seed: augmentedSeed,
            });
            setSuggestedTheme(s.theme || '');
            setImagePrompt(s.imagePrompt || '');
            setTemplateDescription(s.templateDescription || '');
            // Filling the form fires the title onChange handler, so reset
            // notificationTouched first so the suggested message is not overwritten.
            notificationTouched.current = true;
            form.setFieldsValue({
                title: s.title,
                description: s.description,
                startDate: isoToUtcInput(s.startDate),
                duration: s.duration,
                votingDuration: s.votingDuration,
                entryCost: s.entryCost,
                rewards: s.rewards,
                badges: s.badges ?? [],
                roomRequirements: {
                    ...form.getFieldValue('roomRequirements'),
                    minItems: 0,
                    restrictions: s.restrictions ?? [],
                    catalogLocks: s.catalogLocks ?? [],
                },
                renderRequirements: {
                    ...form.getFieldValue('renderRequirements'),
                    requiredLocation: s.renderEnvironment,
                    requiredTimeOfDay: s.renderTimeOfDay,
                },
                notification: { message: s.notificationMessage },
            });
            // Suggested messages count as auto-suggestions, so later prop tweaks keep refreshing them.
            restrictionAutoMsgs.current = Object.fromEntries(
                (s.restrictions ?? []).map((r, i) => [i, r.message]),
            );
            for (const r of s.restrictions ?? []) {
                if (r?.categoryId) ensureCategoryTags(r.categoryId);
            }
            notification.success({ message: `Suggested: ${s.theme}` });
        } catch (e) {
            notification.error({ message: 'Suggest failed', description: e.message });
        } finally {
            setSuggesting(false);
        }
    };

    const handleGenerateImage = async () => {
        const prompt = imagePrompt.trim();
        if (!prompt) {
            notification.warning({ message: 'Image prompt is empty' });
            return;
        }
        setGeneratingImage(true);
        try {
            const { url } = await challengesProxy.suggestImage(prompt);
            setImage(url);
            form.setFieldValue('image', url);
        } catch (e) {
            notification.error({ message: 'Image generation failed', description: e.message });
        } finally {
            setGeneratingImage(false);
        }
    };

    const handleSuggestMessages = async () => {
        const all = form.getFieldValue(['roomRequirements', 'restrictions']) ?? [];
        const rowIndices = all.map((r, i) => (restrictionHasAnyProp(r) ? i : -1)).filter(i => i >= 0);
        if (rowIndices.length === 0) return;
        setSuggestingMessages(true);
        try {
            const { messages } = await challengesProxy.suggestRestrictionMessages(
                rowIndices.map(i => {
                    const { message, ...filters } = all[i];
                    return { ...filters, tag: filters.tag?.trim() || undefined, minItems: Math.max(1, Number(filters.minItems) || 1) };
                }),
            );
            const next = all.slice();
            rowIndices.forEach((rowIndex, k) => {
                const message = messages?.[k];
                if (!message) return;
                next[rowIndex] = { ...next[rowIndex], message };
                // Counts as an auto-suggestion so later filter tweaks keep refreshing it.
                restrictionAutoMsgs.current[rowIndex] = message;
            });
            form.setFieldValue(['roomRequirements', 'restrictions'], next);
            notification.success({ message: 'Restriction messages updated' });
        } catch (e) {
            notification.error({ message: 'Suggest messages failed', description: e.message });
        } finally {
            setSuggestingMessages(false);
        }
    };

    const handleFinish = async (values) => {
        setSubmitting(true);
        try {
            // Strip restriction rows with no filter prop and lock rows with no category.
            const restrictions = (values.roomRequirements?.restrictions ?? [])
                .filter(restrictionHasAnyProp)
                .map(r => ({
                    categoryId: r.categoryId || undefined,
                    color: r.color || undefined,
                    material: r.material || undefined,
                    style: r.style || undefined,
                    roomType: r.roomType || undefined,
                    tag: r.tag?.trim() || undefined,
                    minItems: Math.max(1, Number(r.minItems) || 1),
                    message: r.message?.trim()
                        || suggestRestrictionMessage(r, facetVocab, categoriesById),
                }));
            const catalogLocks = (values.roomRequirements?.catalogLocks ?? [])
                .filter(l => l?.categoryId)
                .map(l => ({
                    categoryId: l.categoryId,
                    lockedMessage: l.lockedMessage?.trim()
                        || defaultLockedMessage(categoriesById.get(l.categoryId)?.title),
                }));
            const badges = (values.badges ?? [])
                .filter(b => b && typeof b.label === 'string' && b.label.trim())
                .map(b => {
                    const out = { label: b.label.trim() };
                    if (b.preset) {
                        out.preset = b.preset;
                    } else if (b.gradient?.from && b.gradient?.to) {
                        out.gradient = { from: b.gradient.from, to: b.gradient.to };
                    }
                    return out;
                });
            // templateItemLocks is edited as a map (itemId -> flags) in local state and merged here.
            const templateItemLocks = readTemplateItemLocks(form);
            // Drop the hidden edit-map scratch field from the body we send to the server.
            const { _templateItemLocksMap, ...cleanValues } = values;  // eslint-disable-line no-unused-vars
            const { categoryRules, ...roomRequirements } = cleanValues.roomRequirements ?? {};  // eslint-disable-line no-unused-vars
            await onSubmit({
                ...cleanValues,
                image,
                startDate: utcInputToIso(values.startDate),
                badges,
                roomRequirements: {
                    ...roomRequirements,
                    restrictions,
                    catalogLocks,
                    templateItemLocks,
                },
            });
        } catch (e) {
            notification.error({ message: 'Save failed', description: e.message });
        } finally {
            setSubmitting(false);
        }
    };

    const merged = {
        ...DEFAULTS,
        ...initialValues,
        rewards: { ...DEFAULTS.rewards, ...(initialValues?.rewards || {}) },
        entryCost: { ...DEFAULTS.entryCost, ...(initialValues?.entryCost || {}) },
        badges: initialValues?.badges ?? [],
        roomRequirements: {
            ...DEFAULTS.roomRequirements,
            ...(initialValues?.roomRequirements || {}),
            restrictions: initialValues?.roomRequirements?.restrictions ?? [],
            catalogLocks: initialValues?.roomRequirements?.catalogLocks ?? [],
            templateItemLocks: initialValues?.roomRequirements?.templateItemLocks ?? [],
        },
        renderRequirements: { ...DEFAULTS.renderRequirements, ...(initialValues?.renderRequirements || {}) },
        notification: { ...DEFAULTS.notification, ...(initialValues?.notification || {}) },
    };

    return (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={merged}
            onFinish={handleFinish}
            onValuesChange={handleValuesChange}
        >
            {specialDay && (
                <Alert
                    type="warning"
                    showIcon
                    banner
                    style={{ marginBottom: 16, fontWeight: 600 }}
                    message={`🎉 Heads up — this challenge falls on ${specialDay}. Theme it accordingly!${enableSuggest ? ' (Auto-suggest will weave it in.)' : ''}`}
                />
            )}
            {enableSuggest && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Auto-suggest a fresh challenge concept"
                    description={
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text type="secondary">
                                Generates a theme that doesn't repeat the last 60 challenges, and fills title,
                                description, schedule, costs, rewards, restrictions, catalog locks, render
                                scene (time of day + outside-the-windows), and notification. The image is
                                generated separately below — review the prompt and click Generate.
                            </Text>
                            <Row gutter={8} style={{ width: '100%' }}>
                                <Col span={10}>
                                    <Select
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        style={{ width: '100%' }}
                                        placeholder="Room (optional — random if empty)"
                                        options={ROOM_TYPE_OPTIONS}
                                        value={suggestRoomType}
                                        onChange={setSuggestRoomType}
                                    />
                                </Col>
                                <Col span={10}>
                                    <Input
                                        allowClear
                                        placeholder="Theme seed (optional, e.g. 'cozy autumn', 'art deco')"
                                        value={suggestSeed}
                                        onChange={(e) => setSuggestSeed(e.target.value)}
                                    />
                                </Col>
                                <Col span={4}>
                                    <Button
                                        type="primary"
                                        icon={<ThunderboltOutlined />}
                                        loading={suggesting}
                                        onClick={handleSuggest}
                                        block
                                    >
                                        Suggest
                                    </Button>
                                </Col>
                            </Row>
                            {suggestedTheme && <Text strong>Theme: {suggestedTheme}</Text>}
                        </Space>
                    }
                />
            )}

            <Title level={4}>Basics</Title>
            <Form.Item label="Title" name="title" rules={[{ required: true }]}>
                <Input onChange={handleTitleChange} />
            </Form.Item>
            <Form.Item label="Description" name="description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item label="Card image" required>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {image && <img src={image} alt="" style={{ width: 200, borderRadius: 6 }} />}
                    {enableSuggest && (
                        <Space direction="vertical" style={{ width: '100%', maxWidth: 600 }}>
                            <Text type="secondary">Suggested image prompt (editable):</Text>
                            <Input.TextArea
                                rows={3}
                                value={imagePrompt}
                                onChange={(e) => setImagePrompt(e.target.value)}
                                placeholder="Click Suggest above to fill, or type your own prompt"
                            />
                            <Button
                                type="primary"
                                icon={image ? <ReloadOutlined /> : <ThunderboltOutlined />}
                                loading={generatingImage}
                                disabled={!imagePrompt.trim()}
                                onClick={handleGenerateImage}
                            >
                                {image ? 'Regenerate image' : 'Generate image'}
                            </Button>
                        </Space>
                    )}
                    <Upload beforeUpload={handleUpload} showUploadList={false} accept="image/*">
                        <Button icon={<UploadOutlined />} loading={uploading}>
                            {image ? 'Replace image (upload)' : 'Upload image'}
                        </Button>
                    </Upload>
                </Space>
            </Form.Item>

            <Divider />
            <Title level={4}>Schedule</Title>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Type" name="type">
                        <Select options={[{ value: 'timebased', label: 'Time-based' }]} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Start date (UTC)" name="startDate">
                        <Input type="datetime-local" />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label="Submission hrs" name="duration">
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label="Voting hrs" name="votingDuration">
                        <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider />
            <Title level={4}>Entry cost</Title>
            <Row gutter={16}>
                <Col span={6}>
                    <Form.Item label="Coins" name={['entryCost', 'coins']}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item label="Gems" name={['entryCost', 'gems']}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider />
            <Title level={4}>Rewards</Title>
            <Text type="secondary">Tier copy is a global constant and the same across all challenges.</Text>
            <div style={{ height: 12 }} />
            {TIERS.map(tier => (
                <Row key={tier} gutter={16} align="middle">
                    <Col span={4}><Text strong style={{ textTransform: 'capitalize' }}>{tier}</Text></Col>
                    <Col span={6}>
                        <Form.Item label="Coins" name={['rewards', tier, 'coins']}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label="Gems" name={['rewards', tier, 'gems']}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label="Avatar item" name={['rewards', tier, 'avatarItemId']}>
                            <AvatarItemSelect />
                        </Form.Item>
                    </Col>
                </Row>
            ))}

            <Divider />
            <Title level={4}>Badges</Title>
            <Text type="secondary">
                Shown as a banner at the bottom of the challenge card on the homepage. Pick a preset
                (which sets color + icon) or use <i>(custom)</i> to render a neutral pill with any label.
            </Text>
            <div style={{ height: 12 }} />
            <Form.List name="badges">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...rest }) => {
                            const badge = watchedBadges[name] ?? {};
                            const isCustom = !badge.preset;
                            return (
                            <div key={key} style={{ marginBottom: 12, padding: 8, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                                <Row gutter={8} align="top" style={{ marginBottom: 8 }}>
                                    <Col span={5}>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'preset']}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                options={BADGE_PRESET_OPTIONS}
                                                placeholder="Preset"
                                                allowClear
                                                onChange={(value) => {
                                                    const badges = form.getFieldValue('badges') ?? [];
                                                    const next = badges.slice();
                                                    const current = next[name] ?? {};
                                                    const presetLabel = value ? BADGE_PRESET_CONFIG[value]?.defaultLabel ?? '' : '';
                                                    next[name] = {
                                                        ...current,
                                                        preset: value || undefined,
                                                        label: presetLabel || current.label || '',
                                                        gradient: value ? undefined : (current.gradient ?? DEFAULT_CUSTOM_GRADIENT),
                                                    };
                                                    form.setFieldValue('badges', next);
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={isCustom ? 11 : 17}>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'label']}
                                            rules={[{ required: true, message: 'Label required' }, noNonBmpRule]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder="Label (shown on the card)" />
                                        </Form.Item>
                                    </Col>
                                    {isCustom && (
                                        <Col span={6}>
                                            <Space size={6} align="center">
                                                <Form.Item
                                                    {...rest}
                                                    name={[name, 'gradient', 'from']}
                                                    style={{ marginBottom: 0 }}
                                                    initialValue={DEFAULT_CUSTOM_GRADIENT.from}
                                                >
                                                    <Input type="color" style={{ width: 40, padding: 2 }} title="Gradient start" />
                                                </Form.Item>
                                                <Form.Item
                                                    {...rest}
                                                    name={[name, 'gradient', 'to']}
                                                    style={{ marginBottom: 0 }}
                                                    initialValue={DEFAULT_CUSTOM_GRADIENT.to}
                                                >
                                                    <Input type="color" style={{ width: 40, padding: 2 }} title="Gradient end" />
                                                </Form.Item>
                                            </Space>
                                        </Col>
                                    )}
                                    <Col span={2}>
                                        <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                    </Col>
                                </Row>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 6,
                                        width: '100%',
                                        padding: '8px 12px',
                                        background: badgePreviewBackground(badge),
                                        color: '#fff',
                                        fontSize: 12,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        lineHeight: 1.1,
                                        textAlign: 'center',
                                        borderRadius: 4,
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                                    }}
                                >
                                    {badge.label?.trim() || <span style={{ opacity: 0.7 }}>(label preview)</span>}
                                </div>
                            </div>
                            );
                        })}
                        <Button type="dashed" onClick={() => add({ label: '', gradient: { ...DEFAULT_CUSTOM_GRADIENT } })} icon={<PlusOutlined />}>
                            Add badge
                        </Button>
                    </>
                )}
            </Form.List>

            <Divider />
            <Title level={4}>Room requirements</Title>
            {enableSuggest && templateDescription && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Suggested template to build manually"
                    description={templateDescription}
                />
            )}
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        label={(
                            <Space size={8}>
                                <span>Template</span>
                                <a
                                    href={`${config().clientBaseUrl}/editor?mode=template`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Create new
                                </a>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    loading={templatesLoading}
                                    onClick={() => loadTemplates()}
                                    title="Refresh templates"
                                />
                            </Space>
                        )}
                        name={['roomRequirements', 'templateId']}
                    >
                        <Select
                            options={templates}
                            allowClear
                            placeholder="None"
                            onChange={loadTemplateItems}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Min items (doors & windows excluded)" name={['roomRequirements', 'minItems']}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Divider />
            <Title level={5}>Restrictions</Title>
            <Text type="secondary">
                Filter-based requirements the user must meet — every set filter must match
                (e.g. category <i>Shelves</i> + color <i>Red</i> + min 3 = "Add 3 red shelves").
                All filters are optional but each row needs at least one. The message is what the
                user sees in the requirements dialog — auto-suggested, edit freely, or click
                <i> Suggest messages</i> to have AI rewrite all of them in short plain words.
            </Text>
            <div style={{ height: 12 }} />
            <Form.List name={['roomRequirements', 'restrictions']}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...rest }) => {
                            const row = watchedRestrictions[name] ?? {};
                            const categoryTags = row.categoryId ? tagsByCategory[row.categoryId] : null;
                            const tagOptions = (categoryTags ?? allTags).map(tag => ({ value: tag }));
                            return (
                            <div key={key} style={{ marginBottom: 12, padding: 8, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                                <Row gutter={8} align="top" style={{ marginBottom: 8 }}>
                                    <Col span={8}>
                                        <Form.Item {...rest} name={[name, 'categoryId']} style={{ marginBottom: 0 }}>
                                            <Select
                                                options={categoryOptions}
                                                showSearch
                                                optionFilterProp="label"
                                                allowClear
                                                placeholder="Category (optional)"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item {...rest} name={[name, 'color']} style={{ marginBottom: 0 }}>
                                            <Select
                                                options={(facetVocab?.colorBuckets ?? []).map(b => ({ value: b.id, label: b.label, hex: b.hex }))}
                                                optionRender={(option) => (
                                                    <Space size={6}>
                                                        <span style={{
                                                            display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                                                            background: `#${option.data.hex}`, border: '1px solid #d9d9d9',
                                                        }} />
                                                        {option.data.label}
                                                    </Space>
                                                )}
                                                showSearch
                                                optionFilterProp="label"
                                                allowClear
                                                placeholder="Color"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item {...rest} name={[name, 'material']} style={{ marginBottom: 0 }}>
                                            <Select
                                                options={(facetVocab?.materials ?? []).map(e => ({ value: e.id, label: e.label }))}
                                                showSearch
                                                optionFilterProp="label"
                                                allowClear
                                                placeholder="Material"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item {...rest} name={[name, 'style']} style={{ marginBottom: 0 }}>
                                            <Select
                                                options={(facetVocab?.styles ?? []).map(e => ({ value: e.id, label: e.label }))}
                                                showSearch
                                                optionFilterProp="label"
                                                allowClear
                                                placeholder="Style"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item {...rest} name={[name, 'roomType']} style={{ marginBottom: 0 }}>
                                            <Select
                                                options={(facetVocab?.roomTypes ?? []).map(e => ({ value: e.id, label: e.label }))}
                                                showSearch
                                                optionFilterProp="label"
                                                allowClear
                                                placeholder="Room"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={8} align="top">
                                    <Col span={5}>
                                        <Form.Item {...rest} name={[name, 'tag']} style={{ marginBottom: 0 }}>
                                            <AutoComplete
                                                options={tagOptions}
                                                filterOption={(input, option) =>
                                                    option.value.toLowerCase().includes(input.toLowerCase())}
                                                allowClear
                                                placeholder={categoryTags ? 'Tag (top 10 for category)' : 'Tag (free text)'}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={3}>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'minItems']}
                                            style={{ marginBottom: 0 }}
                                            rules={[{ required: true, message: 'Min items required' }]}
                                        >
                                            <InputNumber min={1} placeholder="Min items" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={15}>
                                        <Form.Item
                                            {...rest}
                                            name={[name, 'message']}
                                            style={{ marginBottom: 0 }}
                                            dependencies={RESTRICTION_FILTER_PROPS.map(prop =>
                                                ['roomRequirements', 'restrictions', name, prop])}
                                            rules={[
                                                noNonBmpRule,
                                                {
                                                    validator: async () => {
                                                        const r = form.getFieldValue(['roomRequirements', 'restrictions', name]);
                                                        if (!r) return;
                                                        if (!restrictionHasAnyProp(r)) {
                                                            throw new Error('Set at least one filter (category, color, material, style, room, or tag)');
                                                        }
                                                        if (!r.message?.trim()) {
                                                            throw new Error('Message is required');
                                                        }
                                                    },
                                                },
                                            ]}
                                        >
                                            <Input placeholder='User-facing message, e.g. "Add 3 red shelves"' />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1}>
                                        <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                    </Col>
                                </Row>
                            </div>
                            );
                        })}
                        <Space>
                            <Button onClick={() => add({ minItems: 1 })} icon={<PlusOutlined />}>Add restriction</Button>
                            <Button
                                icon={<ThunderboltOutlined />}
                                loading={suggestingMessages}
                                disabled={!watchedRestrictions.some(restrictionHasAnyProp)}
                                onClick={handleSuggestMessages}
                            >
                                Suggest messages
                            </Button>
                        </Space>
                    </>
                )}
            </Form.List>

            <Divider />
            <Title level={5}>Catalog locking</Title>
            <Text type="secondary">
                Lock catalog categories so the user can't change them during the challenge (e.g. walls,
                floor). A lock on a parent category applies to all its descendants. The message is shown
                in the client when the user clicks the locked category.
            </Text>
            <div style={{ height: 12 }} />
            <Form.List name={['roomRequirements', 'catalogLocks']}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.length === 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong>The entire catalog will be opened for this challenge</Text>
                            </div>
                        )}
                        {fields.map(({ key, name, ...rest }) => {
                            const lockRow = watchedCatalogLocks[name] ?? {};
                            return (
                            <Row key={key} gutter={8} align="top" style={{ marginBottom: 8 }}>
                                <Col span={9}>
                                    <Form.Item
                                        {...rest}
                                        name={[name, 'categoryId']}
                                        style={{ marginBottom: 0 }}
                                        rules={[{ required: true, message: 'Category required' }]}
                                    >
                                        <Select
                                            options={categoryOptions}
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Category to lock"
                                        />
                                    </Form.Item>
                                </Col>
                                {lockRow.categoryId && (
                                    <Col span={14}>
                                        <Form.Item {...rest} name={[name, 'lockedMessage']} style={{ marginBottom: 0 }}>
                                            <Input placeholder="Message shown when the locked category is clicked" />
                                        </Form.Item>
                                    </Col>
                                )}
                                <Col span={1}>
                                    <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                                </Col>
                            </Row>
                            );
                        })}
                        <Button onClick={() => add({})} icon={<PlusOutlined />}>Lock a category</Button>
                    </>
                )}
            </Form.List>

            <Divider />
            <Title level={5}>Template item locks</Title>
            <Text type="secondary">
                One row per distinct catalog item in the selected template. Check <b>Pinned</b> to lock every
                instance of that item in place (users can&apos;t move or rotate it); uncheck <b>Can delete</b> to
                also prevent removing it.
            </Text>
            <div style={{ height: 12 }} />
            <TemplateItemLocksTable
                form={form}
                items={templateItems}
                loading={templateItemsLoading}
                initial={initialValues?.roomRequirements?.templateItemLocks ?? []}
                categoriesById={categoriesById}
            />

            <Divider />
            <Title level={4}>Render requirements (optional)</Title>
            <Text type="secondary">
                Overrides for the photorealistic rendering. A set field is hidden from the player's
                Make it real flow; free text is used as-is in the prompt — e.g. "sunrise over the
                desert", "rainy night in Tokyo". "What should we make" defaults to Photo; "User's
                choice" shows the photo/video picker in the challenge flow.
            </Text>
            <div style={{ height: 12 }} />
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label="Time of day" name={['renderRequirements', 'requiredTimeOfDay']}>
                        <Input allowClear placeholder="e.g. golden hour, midnight, foggy dawn" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Outside the windows" name={['renderRequirements', 'requiredLocation']}>
                        <AutoComplete allowClear options={WINDOW_VIEW_OPTIONS} placeholder="Pick a view or type your own" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="What should we make" name={['renderRequirements', 'requiredMediaType']}>
                        <Select options={MEDIA_TYPE_OPTIONS} />
                    </Form.Item>
                </Col>
            </Row>
            {(watchedMediaType === 'video' || watchedMediaType === 'any') && (
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            label="Video filter"
                            name={['renderRequirements', 'videoFilter']}
                            extra={
                                VIDEO_FILTER_OPTIONS.find((f) => f.value === watchedVideoFilter)?.description
                                ?? 'Applied to every video made for this challenge, with its own music. Players never choose it.'
                            }
                        >
                            <Select allowClear placeholder="None (regular look and music)" options={VIDEO_FILTER_OPTIONS} />
                        </Form.Item>
                    </Col>
                </Row>
            )}

            <Divider />
            <Title level={4}>Notification</Title>
            <Form.Item
                label="Message (sent when rewards are ready)"
                name={['notification', 'message']}
                rules={[{ required: true }, noNonBmpRule]}
            >
                <Input onChange={() => { notificationTouched.current = true; }} />
            </Form.Item>

            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    disabled={!image}
                >
                    {submitLabel}
                </Button>
            </Form.Item>
        </Form>
    );
}

// ---------- Template item locks table ----------

/**
 * Read-only helper invoked at submit time: walks the form's hidden `_templateItemLocksMap`
 * field and returns only rows that actually restrict something.
 */
function readTemplateItemLocks(form) {
    const map = form.getFieldValue('_templateItemLocksMap') ?? {};
    const out = [];
    for (const [catalogItemId, flags] of Object.entries(map)) {
        const isPinned  = flags?.isPinned  ?? false;
        const canDelete = flags?.canDelete ?? true;
        if (!isPinned && canDelete) continue;
        out.push({ catalogItemId, isPinned, canDelete });
    }
    return out;
}

/** Pre-DES-114 rows stored canMove/canRotate — treat either being false as pinned. */
function isLegacyLockPinned(lock) {
    return lock.isPinned ?? !((lock.canMove ?? true) && (lock.canRotate ?? true));
}

function TemplateItemLocksTable({ form, items, loading, initial, categoriesById }) {
    // Canonical edit state — checkboxes are controlled against this. Mirrored into a hidden
    // form field on every change so the `handleFinish` submit handler can read it alongside
    // the rest of the form values.
    const [locksMap, setLocksMap] = useState({});

    // Seed from `initial` whenever (a) items load for the first time or (b) the initial
    // prop itself changes (e.g. admin navigates between challenges). We preserve any
    // unsaved in-progress edits the admin already made in this session.
    useEffect(() => {
        if (!items || items.length === 0) return;
        setLocksMap(prev => {
            const seeded = { ...prev };
            for (const lock of initial) {
                if (!seeded[lock.catalogItemId]) {
                    seeded[lock.catalogItemId] = {
                        isPinned:  isLegacyLockPinned(lock),
                        canDelete: lock.canDelete ?? true,
                    };
                }
            }
            form.setFieldValue('_templateItemLocksMap', seeded);
            return seeded;
        });
    }, [items, initial, form]);

    const getFlag = (catalogItemId, flag, fallback) => locksMap[catalogItemId]?.[flag] ?? fallback;
    const setFlag = (catalogItemId, flag, value) => {
        setLocksMap(prev => {
            const existing = prev[catalogItemId] ?? { isPinned: false, canDelete: true };
            const next = { ...prev, [catalogItemId]: { ...existing, [flag]: value } };
            form.setFieldValue('_templateItemLocksMap', next);
            return next;
        });
    };

    const columns = [
        { title: 'Name', dataIndex: 'name' },
        { title: 'Type', dataIndex: 'type', width: 110 },
        { title: 'Category', key: 'category', render: (_, row) => categoryPath(row.categoryId, categoriesById) },
        { title: 'Count', dataIndex: 'count', width: 70 },
        {
            title: 'Pinned',
            key: 'isPinned',
            width: 110,
            render: (_, row) => (
                <Checkbox
                    checked={getFlag(row.catalogItemId, 'isPinned', false)}
                    onChange={e => setFlag(row.catalogItemId, 'isPinned', e.target.checked)}
                />
            ),
        },
        {
            title: 'Can delete',
            key: 'canDelete',
            width: 110,
            render: (_, row) => (
                <Checkbox
                    checked={getFlag(row.catalogItemId, 'canDelete', true)}
                    onChange={e => setFlag(row.catalogItemId, 'canDelete', e.target.checked)}
                />
            ),
        },
    ];

    if (!items || items.length === 0) {
        return <Text type="secondary">Select a template to configure per-item locks.</Text>;
    }

    return (
        <>
            {/* Scratch form field — `handleFinish` reads via `readTemplateItemLocks` and strips it from the submit body. */}
            <Form.Item name="_templateItemLocksMap" hidden initialValue={{}}>
                <Input type="hidden" />
            </Form.Item>
            <Table
                dataSource={items}
                columns={columns}
                rowKey="catalogItemId"
                pagination={false}
                size="small"
                loading={loading}
            />
        </>
    );
}
